import { Views } from '@singularsystems/neo-react';
import ProvisionalTaxReportsSearchCriteria from '../Models/Queries/ProvisionalTaxReportsSearchCriteria';
import { AppService, Types } from '../../../App/Services/AppService';
import TaxSummaryVM from '../Components/TaxSummaryVM';
import RealisedGainsSummaryVM from '../Components/RealisedGainsSummaryVM';
import DividendTransactionsVM from '../Components/DividendTransactionsVM';
import InterestTransactionsVM from '../Components/InterestTransactionsVM';
import InterestWithholdingTaxVM from '../Components/InterestWithholdingTaxVM';
import DividendWithholdingTaxVM from '../Components/DividendWithholdingTaxVM';
import DataValidationLookup from '../Models/Lookups/DataValidationLookup';
import FileDownloadApiClient from '../../../Common/ApiClients/FileDownloadApiClient';
import UnrealisedGainsSummaryVM from '../Components/UnrealisedGainsSummaryVM';
import { StaticDataTypes } from '../../../Maintenance/StaticData/StaticDataTypes';
import { Model } from '@singularsystems/neo-core';
import ClientLookup from '../../../DataInterception/ClientAccountMapping/Models/ClientLookup';

export default class ProvisionalTaxReportsVM extends Views.ViewModelBase {
    public provisionalTaxReportsLookupCriteria = new ProvisionalTaxReportsSearchCriteria();    
    
    public taxSummaryVM = this.registerViewModel(TaxSummaryVM);
    public realisedGainsSummaryVM = this.registerViewModel(RealisedGainsSummaryVM);
    public unrealisedGainsSummaryVM = this.registerViewModel(UnrealisedGainsSummaryVM);
    public dividendTransactionsVM = this.registerViewModel(DividendTransactionsVM); 
    public dividendWithholdingTaxVM = this.registerViewModel(DividendWithholdingTaxVM);
    public interestTransactionsVM = this.registerViewModel(InterestTransactionsVM);
    public interestTransactionsWithholdingTaxVM = this.registerViewModel(InterestWithholdingTaxVM);
    public fileDownloadApiClient = new FileDownloadApiClient();
    public validationMessageList: string[] = [];
    public showDataValidationModal: boolean = false;
    public showTabs: boolean = false;   
    public dataValidationLookup: DataValidationLookup = new DataValidationLookup();
       
    public clientAccounts: Model.PlainObject<ClientLookup>[] = [];
    public filteredClientAccounts: Model.PlainObject<ClientLookup>[] = [];    

    constructor(
        public taskRunner = AppService.get(Types.Neo.TaskRunner),
        public serviceProviderApiClient = AppService.get(Types.ApiClients.ServiceProviderApiClient),
        public provisionalTaxReportsApiClient = AppService.get(Types.ApiClients.ProvisionalTaxReportsApiClient),
        private staticDataCache = AppService.get(StaticDataTypes.Services.StaticDataCache)) {

        super(taskRunner);
        this.makeObservable();
    }

    public async initialise() {
        await this.populateClientAccounts();        
    }

    public async populateClientAccounts() {
        this.clientAccounts = await this.staticDataCache.clientAccounts.getDataAsync()
            .then((arr) => arr.sort((x, y) => x.clientDescription.localeCompare(y.clientDescription)));
        this.filteredClientAccounts = this.clientAccounts.slice(0, 10);
    }

    public mapClientNumberAndYear() {
        this.provisionalTaxReportsLookupCriteria.financialYear = this.provisionalTaxReportsLookupCriteria.finYearDate?.getFullYear().toString();
        this.provisionalTaxReportsLookupCriteria.clientNumber = this.provisionalTaxReportsLookupCriteria.clientNumber.split(' | ')[0];      
    }

    public async search() {
        let currentClientNumber = this.provisionalTaxReportsLookupCriteria.clientNumber; 
        this.mapClientNumberAndYear();                
        this.dataValidationLookup.validationMessages = "";
        this.taxSummaryVM.search(this.provisionalTaxReportsLookupCriteria);
        this.realisedGainsSummaryVM.search(this.provisionalTaxReportsLookupCriteria);
        this.unrealisedGainsSummaryVM.search(this.provisionalTaxReportsLookupCriteria);
        this.dividendTransactionsVM.search(this.provisionalTaxReportsLookupCriteria);
        this.dividendWithholdingTaxVM.search(this.provisionalTaxReportsLookupCriteria);
        this.interestTransactionsVM.search(this.provisionalTaxReportsLookupCriteria);        
        this.interestTransactionsWithholdingTaxVM.search(this.provisionalTaxReportsLookupCriteria);    

        await this.taskRunner.run(async () => {           
            await this.taxSummaryVM.taxSummaryPageManager.refreshData();
            await this.dividendTransactionsVM.dividendTransactionsPageManager.refreshData();
            await this.dividendWithholdingTaxVM.dividendWithholdingTaxPageManager.refreshData();
            await this.realisedGainsSummaryVM.realisedGainsSummaryPageManager.refreshData();
            await this.unrealisedGainsSummaryVM.unrealisedGainsSummaryPageManager.refreshData();
            await this.interestTransactionsVM.interestTransactionsPageManager.refreshData();
            await this.interestTransactionsWithholdingTaxVM.interestWithholdingTaxPageManager.refreshData();
         
            let dataValidationResponse = (await this.provisionalTaxReportsApiClient.getDataValidations(this.provisionalTaxReportsLookupCriteria.toQueryObject())).data;
            this.mapDataValidationResult(dataValidationResponse);

            this.showTabs = true;

            this.provisionalTaxReportsLookupCriteria.clientNumber = currentClientNumber;
        });

        this.showDataValidationModal = this.validationMessageList.every(msg => msg !== '');
    }

    public mapDataValidationResult(dataValidationResponse: any) {
       this.validationMessageList = dataValidationResponse.validationMessages.split('|');             
    }

    public getValidationMessage() {

    }

    public closeValidationModal() {    
        this.showDataValidationModal = false;
    }

    public async downloadProvisionalTaxReport() {
        this.mapClientNumberAndYear();        
        const response = await this.taskRunner.waitFor(this.fileDownloadApiClient.downloadProvisionalTaxReportsExcel(this.provisionalTaxReportsLookupCriteria.toJSObject()));                
    }
}